import { useEffect } from "react";
import { motion } from "framer-motion";
import { Clock3, MessageCircle, FileCheck2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { SITE, waLink } from "../data/site";
import { track } from "../lib/analytics";

const NOTES = [
    { icon: Clock3, title: "Resposta rápida", desc: "Retornamos em horário comercial com as opções para o seu projeto." },
    { icon: FileCheck2, title: "Sem compromisso", desc: "O orçamento é gratuito — você decide depois de comparar." },
    { icon: MessageCircle, title: "Direto no WhatsApp", desc: "Prefere conversar? Chame a qualquer momento pelo botão de WhatsApp." },
];

export default function Orcamento() {
    useEffect(() => track("page_view", { page: "orcamento" }), []);

    return (
        <main className="bg-ink">
            <Seo
                title="Solicitar Orçamento | IMPRIMA AKI — Gráfica em Campinas"
                description="Solicite seu orçamento de materiais gráficos, comunicação visual, camisetas, uniformes, placas e adesivos em Campinas – SP. Resposta rápida pelo WhatsApp."
            />

            <section data-testid="orcamento-hero" className="grain relative overflow-hidden pt-32 sm:pt-40 pb-12 text-white">
                <div className="hero-grid-bg absolute inset-0" />
                <div className="container-x relative max-w-3xl">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="section-tag flex items-center gap-3"
                    >
                        <span className="inline-block h-px w-8 bg-brand" />
                        Orçamento
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04]"
                    >
                        Conte o que você precisa. <span className="text-brand">A gente resolve.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.22 }}
                        className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-mist"
                    >
                        Preencha os dados abaixo. Sua solicitação fica registrada e você pode continuar a conversa no
                        WhatsApp com tudo já organizado.
                    </motion.p>
                </div>
            </section>

            <section className="bg-paper py-16 sm:py-24">
                <div className="container-x grid gap-10 lg:grid-cols-12">
                    <FadeUp className="lg:col-span-8">
                        <div className="rounded-2xl border border-[#E9E9E9] bg-white p-6 sm:p-10 shadow-[0_30px_80px_-40px_rgba(13,13,13,0.25)]">
                            <QuoteForm />
                        </div>
                    </FadeUp>
                    <div className="lg:col-span-4 space-y-5">
                        {NOTES.map((n, i) => (
                            <FadeUp key={n.title} delay={0.1 + i * 0.08}>
                                <div className="rounded-2xl border border-[#E9E9E9] bg-white p-6">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-tint text-brand">
                                        <n.icon size={19} strokeWidth={1.8} />
                                    </div>
                                    <h3 className="mt-4 font-display text-base font-bold text-ink">{n.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-smoke">{n.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                        <FadeUp delay={0.35}>
                            <a
                                href={waLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => track("whatsapp_click", { location: "orcamento_page" })}
                                data-testid="orcamento-whatsapp-side"
                                className="flex items-center justify-between rounded-2xl bg-ink p-6 text-white transition-colors hover:bg-coal"
                            >
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">WhatsApp comercial</p>
                                    <p className="mt-1 font-display text-lg font-bold">{SITE.phoneDisplay}</p>
                                </div>
                                <MessageCircle size={22} className="text-brand" fill="currentColor" />
                            </a>
                        </FadeUp>
                    </div>
                </div>
            </section>
        </main>
    );
}
